import { Suspense, useMemo } from 'react'
import { CertificateRenderer, type CertificateRendererCertificate, type CertificateRendererProps } from '@jmt/talent-renderer'
import { useTranslation } from 'react-i18next'

import siegel from '../../assets/img/certified_pos.png'
import type { Certificate, Talent } from '../../lib/apollo/types'

import BorussiaShanghaiCertificate from './BorussiaShanghai'
import HsvCertificate from './HSV'
import RBLCertificate from './RBL'

export type CertificateProps = {
    certificate: Certificate,
    talent?: Talent,
}

type CertificateClubConfig = Omit<
    CertificateRendererProps,
    'certificate' | 'club' | 'clubName' | 'footer' | 'talentReportTitle'
> & {
    clubName: string
    footer?: CertificateRendererProps['footer']
    talentReportTitle?: string
}

type CertificateRenderPayload = {
    renderer?: string
    certificate?: Partial<CertificateRendererCertificate> & {
        camp?: Partial<CertificateRendererCertificate['camp']>
    }
    renderConfig?: Partial<Omit<CertificateRendererProps, 'certificate'>> & {
        club?: string
        clubName?: string
    }
    designSnapshot?: {
        cssText?: string
    }
}

const defaultClubConfig: CertificateClubConfig = {
    designId: 'jmt-default',
    clubName: 'Join My Talent',
    clubImg: '/assets/logo_with_text.svg',
    schoolImg: '/assets/logo_jmt.svg',
    defaultTalentImage: '/assets/default_talentcard.png',
    showDateInCertificate: false,
    talentReportTitle: 'Talent Report',
    talentReportTitleTwo: '',
    translationNs: 'translation',
    header: {
        intro: '',
    },
    footer: {
        img: '/assets/logo_with_text.svg',
        imgRight: '/assets/icon/logo.svg',
    },
}

const clubConfigs: Record<string, CertificateClubConfig> = {
    hsv: {
        ...defaultClubConfig,
        designId: 'hsv',
        clubName: 'HSV Fussballschule',
        clubImg: '/assets/icon/hsv_logo.svg',
        schoolImg: '/assets/hsv-fubaschu.jpeg',
        sigil: siegel,
        talentReportTitle: 'HSV Talentcard',
        footer: {
            img: '/assets/icon/hsv_logo.svg',
            imgRight: '/assets/icon/logo.svg',
        },
    },
    rbl: {
        ...defaultClubConfig,
        designId: 'rbl',
        clubName: 'RB Leipzig Fussballschule',
        clubImg: '/assets/rbl/schule_logo.png',
        schoolImg: '/assets/rbl/logo.svg',
        additionalLogo: '/assets/rbl/logo.svg',
        reportBackgroundImage: '/assets/rbl/cert_bg.jpeg',
        talentReportTitle: 'Talent Report',
        footer: {
            img: '/assets/rbl/schule_logo.png',
            imgRight: '/assets/icon/logo.svg',
        },
    },
}

const toRendererCertificate = (certificate: Certificate): CertificateRendererCertificate => ({
    id: certificate.id,
    participantId: certificate.talentId,
    caption: certificate.caption ?? `${certificate.firstName} ${certificate.lastName}`,
    firstName: certificate.firstName,
    lastName: certificate.lastName,
    birthday: certificate.birthday,
    image: certificate.image,
    vote: certificate.vote,
    talentClass: certificate.talentClass ?? undefined,
    camp: {
        caption: certificate.camp.caption,
        start: certificate.camp.start,
        end: certificate.camp.end,
        campType: certificate.camp.campType,
    },
})

const parseRenderPayload = (renderPayload?: string | null): CertificateRenderPayload | undefined => {
    if (!renderPayload) return undefined

    try {
        const parsed = JSON.parse(renderPayload)

        if (parsed?.renderer !== '@jmt/talent-renderer') return undefined

        return parsed
    } catch {
        return undefined
    }
}

const mergeRendererCertificate = (
    certificate: Certificate,
    payload?: CertificateRenderPayload,
): CertificateRendererCertificate => {
    const fallback = toRendererCertificate(certificate)
    const payloadCertificate = payload?.certificate

    if (!payloadCertificate) return fallback

    return {
        ...fallback,
        ...payloadCertificate,
        camp: {
            ...fallback.camp,
            ...(payloadCertificate.camp ?? {}),
        },
    }
}

const mergeRendererConfig = (
    baseConfig: CertificateClubConfig,
    payload?: CertificateRenderPayload,
): CertificateClubConfig => {
    const renderConfig = payload?.renderConfig

    if (!renderConfig) return baseConfig

    return {
        ...baseConfig,
        ...renderConfig,
        clubName: renderConfig.clubName ?? baseConfig.clubName,
        header: {
            ...baseConfig.header,
            ...(renderConfig.header ?? {}),
        },
        footer: renderConfig.footer || baseConfig.footer
            ? {
                ...(baseConfig.footer ?? {}),
                ...(renderConfig.footer ?? {}),
            }
            : undefined,
    }
}

const getRuntimeCertificateCss = (payload?: CertificateRenderPayload): string | undefined => {
    const cssText = payload?.designSnapshot?.cssText?.trim()
    return cssText || undefined
}

const CertificateComponent: React.FC<CertificateProps> = ({ certificate, talent }) => {
    const { t } = useTranslation()

    const renderPayload = useMemo(() => parseRenderPayload(certificate.renderPayload), [certificate.renderPayload])
    const config = useMemo(() => mergeRendererConfig(clubConfigs[certificate.club] ?? defaultClubConfig, renderPayload), [certificate.club, renderPayload])
    const rendererCertificate = useMemo(() => mergeRendererCertificate(certificate, renderPayload), [certificate, renderPayload])
    const rendererClub = renderPayload?.renderConfig?.club ?? certificate.club
    const runtimeCertificateCss = useMemo(() => getRuntimeCertificateCss(renderPayload), [renderPayload])

    if (certificate.club === 'hsv') {
        return (
            <HsvCertificate
                certificate={certificate}
                header={{
                    intro: 'Bei uns bist du der Star',
                    title: 'HSV TALENTCARD',
                }}
                footer={{
                    text: t('certificate.footer') as string,
                    img: '/assets/icon/hsv_logo.svg',
                }}
                text='Toll, dass Du mitgemacht hast. Deine digitale Talentcard findest Du hinter dem QR-Code oder hier:'
                clubImg='/assets/icon/hsv_logo.svg'
                schoolImg='/assets/hsv-fubaschu.jpeg'
            />
        )
    }

    if (certificate.club === 'rbl') {
        return (
            <RBLCertificate
                certificate={certificate}
                talent={talent}
            />
        )
    }

    if (certificate.club === 'borussia-shanghai') {
        return (
            <BorussiaShanghaiCertificate
                {...certificate}
                talent={talent}
            />
        )
    }

    return (
        <Suspense fallback='...loading'>
            <div className='jmt-certificate-runtime'>
                {runtimeCertificateCss && <style>{runtimeCertificateCss}</style>}
                <CertificateRenderer
                    {...config}
                    certificate={rendererCertificate}
                    club={rendererClub}
                    clubName={config.clubName}
                    talentReportTitle={config.talentReportTitle ?? t('certificate.title', 'Talent Report')}
                    footer={config.footer
                        ? {
                            ...config.footer,
                            text: config.footer.text ?? t('certificate.footer', 'certificate.footer', {
                                clubShort: rendererClub,
                                tenantName: config.clubName,
                            }),
                        }
                        : undefined}
                />
            </div>
        </Suspense>
    )
}

export default CertificateComponent
